/**
 * Integration ownership — the vendor-neutral vocabulary
 *
 * A record in Shaka is either **locally owned** (created here, fully editable)
 * or **integration-owned** (mirrored from an external system, which is the
 * source of truth for its fields).
 *
 * Today the only integration is Bookinglayer, but the domain must not say so.
 * Modules ask `isIntegrationOwned(record)`; only the surfaces that *brand* a
 * vendor — an icon, a label, a deep link — name it.
 *
 * See `docs/PROJECT_STATE.md` › "Deck ↔ Bookinglayer".
 */

/** The one source that means "this is ours". Everything else is an integration. */
export const LOCAL_SOURCE = 'manual' as const;

/**
 * Where a record came from. Open by design: a value this union does not list
 * is still an integration, and must be treated as integration-owned rather
 * than silently collapsed into `'manual'` — mislabelling a mirrored record as
 * local makes it look editable, and edits to it are lost on the next sync.
 */
export type IntegrationSource = typeof LOCAL_SOURCE | 'bookinglayer' | (string & {});

/** Backend-computed ownership. Absent on payloads that predate the field. */
export interface OwnershipInfo {
  source: IntegrationSource;
  isIntegrationOwned: boolean;
  isLocal: boolean;
  /**
   * How the owning system calls itself, for the provenance badge.
   * Optional: not every payload that carries ownership carries a brand.
   */
  label?: string;
  /**
   * Where to open this record in the system that owns it. Empty when there is
   * nowhere to go.
   *
   * The backend builds it. Until 2026-09-11 the deck hardcoded
   * `https://app.bookinglayer.io/orders/` in three separate files, so adding a
   * second integration meant editing screens rather than installing it.
   */
  externalUrl?: string;
}

/**
 * The minimum a record has to expose to be asked about its ownership.
 * Both fields are optional: older payloads carry only the flat `source`,
 * newer ones carry `ownership`, and some carry both.
 */
export interface IntegrationOwnedRecord {
  ownership?: Pick<OwnershipInfo, 'source' | 'isIntegrationOwned'> | Partial<OwnershipInfo> | null;
  source?: IntegrationSource | null;
}
